import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { FileRetrieveResponse } from '@types';
import { FileUploadItem } from '@utils';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import { CardHeader, CardTitle } from '../card/Card';
import { ButtonQHDS, LoadingSpinnerQldDS } from '../qldDesignSystem';
import { NextButton, PrevButton, usePrevNextButtons } from './CarouselArrowButtons';
import { DotButton, useDotButton } from './CarouselDotButton';

import styles from './imageCarousel.module.scss';

type ImageCarouselProps = {
	/** Array of FileUploadItem objects representing the files to be displayed in the carousel */
	files: FileUploadItem[];
	/** Function to update the files array */
	updateFiles?: (updatedFiles: FileUploadItem[]) => void;
	/** Function to retrieve a file given its entity ID and file URI */
	retrieveFile?: (fileIdentifier: string) => Promise<FileRetrieveResponse>;
	/** Optional configuration options for the Embla Carousel */
	options?: EmblaOptionsType;
};

export const ImageCarousel: React.FC<ImageCarouselProps> = props => {
	const { files, updateFiles, retrieveFile, options } = props;
	const [emblaRef, emblaApi] = useEmblaCarousel(options);

	const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
	const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
		usePrevNextButtons(emblaApi);
	const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
	const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

	const imageSources = useMemo(() => {
		return files
			.map(file => ({
				filename: file.file?.name || file.filename || '',
				src: file.file ? URL.createObjectURL(file.file) : (file.base64 ?? ''),
			}))
			.filter(item => item.src !== '');
	}, [files]);

	const fetchImage = async (file: FileUploadItem) => {
		const filename = file.file?.name || file.filename || '';
		if (file.base64) return file;

		setImageLoading(prev => ({ ...prev, [filename]: true }));
		try {
			if (file.file) {
				const base64 = await fileToBase64(file.file);
				return { ...file, base64 };
			}

			if (typeof retrieveFile === 'function' && file.fileLocation?.identifier) {
				const { content } = await retrieveFile(file.fileLocation.identifier);
				return {
					...file,
					base64: `data:image/${filename.split('.').pop()};base64,${content}`,
				};
			} else {
				setImageErrors(prev => ({ ...prev, [filename]: true }));
			}
			return file;
		} catch (error) {
			console.error(`Failed to fetch image for ${filename}:`, error);
			setImageErrors(prev => ({ ...prev, [filename]: true }));
			return file;
		} finally {
			setImageLoading(prev => ({ ...prev, [filename]: false }));
		}
	};

	useEffect(() => {
		const fetchImages = async () => {
			// Filter files that need fetching - not already fetched and not already failed
			const filesToFetch = files.filter(file => {
				const filename = file.file?.name || file.filename || '';
				return !file.base64 && !imageErrors[filename];
			});

			// If there are files to fetch and we can update files
			if (filesToFetch.length > 0 && updateFiles) {
				// Process all files, fetching only those that need it
				const updatedFiles = await Promise.all(
					files.map(async file => {
						const filename = file.file?.name || file.filename || '';
						return file.base64 || imageErrors[filename] ? file : await fetchImage(file);
					}),
				);

				// Update files with new data
				updateFiles(updatedFiles);
			}
		};

		fetchImages();

		// Clear errors and loading state for removed files
		setImageErrors(prev => {
			const newErrors = { ...prev };
			Object.keys(newErrors).forEach(filename => {
				if (!files.some(file => (file.file?.name || file.filename) === filename)) {
					delete newErrors[filename];
				}
			});
			return newErrors;
		});

		setImageLoading(prev => {
			const newLoading = { ...prev };
			Object.keys(newLoading).forEach(filename => {
				if (!files.some(file => (file.file?.name || file.filename) === filename)) {
					delete newLoading[filename];
				}
			});
			return newLoading;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [files]);

	const fileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = error => reject(error);
		});
	};

	if (files.length === 0) {
		return null;
	}

	return (
		<>
			<section className={styles.carousel}>
				<div className={styles.carouselViewport} ref={emblaRef}>
					<div className={styles.carouselContainer}>
						{files.map((file, index) => {
							const filename = file.file?.name || file.filename || '';
							return (
								<div className={styles.carouselSlide} key={index}>
									<div className={styles.imageContainer}>
										{imageLoading[filename] ? (
											<div className={styles.loaderContainer}>
												<LoadingSpinnerQldDS height="40" width="40" />
											</div>
										) : imageErrors[filename] ? (
											<div className={styles.errorMessage}>Failed to load image</div>
										) : imageSources[index] ? (
											<>
												<Image
													src={imageSources[index].src}
													alt={`Uploaded image ${filename}`}
													className={styles.image}
													fill
													onError={() =>
														setImageErrors(prev => ({
															...prev,
															[filename]: true,
														}))
													}
												/>
												{imageSources[index].filename && (
													<div className={styles.imageCaption}>{imageSources[index].filename}</div>
												)}
											</>
										) : null}
									</div>
								</div>
							);
						})}
					</div>
				</div>

				{files.length > 1 && (
					<div className={styles.carouselControls}>
						<div className={styles.carouselButtons}>
							<PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
							<NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
						</div>

						<div className={styles.carouselDots}>
							{scrollSnaps.map((_, index) => (
								<DotButton
									key={index}
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										onDotButtonClick(index);
									}}
									className={[
										styles.carouselDot,
										index === selectedIndex ? styles.carouselDotSelected : '',
									].join(' ')}
								/>
							))}
						</div>
					</div>
				)}
			</section>

			{/* Report layout */}
			<section className={styles.report}>
				{files.map((file, index) => {
					const filename = file.file?.name || file.filename || '';
					return (
						<div className={styles.imageContainerOnReport} key={`report-photo-${index}`}>
							{imageErrors[filename] ? (
								<div>Failed to load image</div>
							) : imageSources[index] ? (
								<>
									{/* Title on each page */}
									<CardHeader className={styles.cardHeader}>
										<div className={styles.summaryCardHeader}>
											<CardTitle className={styles.summaryCardTitle}>Photos</CardTitle>
											<ButtonQHDS customStyles={styles.invisibleButton}>
												<p></p>
											</ButtonQHDS>
										</div>
										<div className={styles.cardSeparator} />
									</CardHeader>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src={imageSources[index].src}
										alt={`Uploaded image ${filename}`}
										className={styles.imageOnReport}
									/>
									{imageSources[index].filename && (
										<div className={styles.imageCaptionOnReport}>
											{imageSources[index].filename}
										</div>
									)}
								</>
							) : (
								<div>Image not loaded</div>
							)}
						</div>
					);
				})}
			</section>
		</>
	);
};
